import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Type, Image, Plus, Trash2, Save, RefreshCw, Eye, EyeOff, X, Layers
} from "lucide-react";



const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const AdminFeatureTwo = () => {
  const [data, setData] = useState({
    subTitle: "",
    mainTitle: "",
    backgroundShapes: { topShape: "", lineShape: "" },
    steps: [],
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/feature-two`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Handle Simple Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  // Handle Nested Object Change (Backgrounds)
  const handleNestedChange = (category, field, value) => {
    setData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Handle Step Change
  const handleStepChange = (index, field, value) => {
    const newSteps = [...data.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setData({ ...data, steps: newSteps });
  };

  const addStep = () => {
    const newStep = {
      id: Date.now().toString(),
      stepLabel: `STEP-0${data.steps.length + 1}`,
      title: "",
      description: "",
      altStyle: false
    };
    setData({ ...data, steps: [...data.steps, newStep] });
  };

  const removeStep = (index) => {
    const newSteps = data.steps.filter((_, i) => i !== index);
    setData({ ...data, steps: newSteps });
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/feature-two`, data);
      showMessage("Section updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default settings?")) return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/feature-two/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Reset successful", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/feature-two/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(`Section ${newStatus ? "activated" : "deactivated"}`, "success");
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">"How It Works" Section Management</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${data.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={toggleStatus}
            >
                {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                {data.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving}>
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* Content Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Type size={20} /> Section Content
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Sub Title</label>
                            <input
                              type="text"
                              name="subTitle"
                              className="form-control"
                              value={data.subTitle}
                              onChange={handleChange}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Main Title</label>
                            <input
                              type="text"
                              name="mainTitle"
                              className="form-control"
                              value={data.mainTitle}
                              onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Steps Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <Layers size={20} /> Steps
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addStep}>
                        <Plus size={16} /> Add Step
                    </button>
                </div>
                <div className="card-body">
                    {data.steps && data.steps.map((step, index) => (
                        <div key={step.id || index} className="row g-2 align-items-start mb-3 pb-3 border-bottom">
                            <div className="col-12 mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="badge bg-secondary d-flex align-items-center gap-1"><Layers size={14} /> {step.stepLabel || `Step ${index + 1}`}</span>
                                    <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeStep(index)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">Step Label (e.g. STEP-01)</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Step Label (e.g. STEP-01)"
                                  value={step.stepLabel}
                                  onChange={(e) => handleStepChange(index, 'stepLabel', e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">Title</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Title"
                                  value={step.title}
                                  onChange={(e) => handleStepChange(index, 'title', e.target.value)}
                                />
                            </div>
                            <div className="col-12 mb-2">
                                <label className="small text-muted">Description</label>
                                <textarea
                                  className="form-control form-control-sm"
                                  rows="2"
                                  placeholder="Description"
                                  value={step.description}
                                  onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                />
                            </div>
                            <div className="col-12">
                                <div className="form-check form-switch d-flex align-items-center gap-2">
                                    <input
                                      className="form-check-input mt-0"
                                      type="checkbox"
                                      id={`style-${index}`}
                                      checked={step.altStyle}
                                      onChange={(e) => handleStepChange(index, 'altStyle', e.target.checked)}
                                    />
                                    <label className="form-check-label small mb-0" htmlFor={`style-${index}`}>Alternate Style (Dark Background)</label>
                                </div>
                            </div>
                        </div>
                    ))}
                    {(!data.steps || data.steps.length === 0) && <p className="text-center text-muted py-3">No steps added.</p>}
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminFeatureTwo;